import Disclaimer from "../components/Disclaimer";
import { CallCard } from "../components/PhoneCTA";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import Breadcrumbs from "../components/Breadcrumbs";
import { Video, Calendar, ClipboardCheck, MapPinned } from "lucide-react";

const steps = [
  { icon: ClipboardCheck, title: "Educational assessment", desc: "Explore peptide categories aligned with your goals. Complete an optional questionnaire to help the provider prepare." },
  { icon: Calendar,      title: "Schedule your visit",    desc: "Choose a live video consultation time. Available across all states VidaVital serves." },
  { icon: Video,         title: "Provider consultation",  desc: "A licensed VidaVital clinician reviews your history, goals, and any recent labs on a secure video visit." },
  { icon: MapPinned,     title: "Ongoing follow-up",      desc: "If a therapy is prescribed, follow-ups and lab reviews happen on the same secure platform." },
];

export default function Telemedicine() {
  return (
    <div data-testid="telemedicine-page">
      <SEO
        title="Telemedicine Peptide Therapy Consultations by Video"
        description="Meet a licensed VidaVital Medical provider by live secure video. Learn what we treat via telehealth, how visits work, HSA/FSA eligibility, and state licensure."
        path="/telemedicine"
        breadcrumbs={[{ name: "Telemedicine", path: "/telemedicine" }]}
      />
      <section className="vv-hero">
        <div className="vv-container py-16">
          <Breadcrumbs items={[{ name: "Telemedicine", path: "/telemedicine" }]} />
          <div className="vv-hero-eyebrow">Telemedicine</div>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl mt-3">Meet a provider from anywhere.</h1>
          <p className="mt-4 max-w-2xl">VidaVital Medical delivers licensed peptide, hormone, and wellness consultations by live secure video, plus in-clinic visits at five locations.</p>
        </div>
      </section>

      <section className="vv-container py-14 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
        {steps.map((s, i) => (
          <div key={s.title} data-testid={`tele-step-${i}`} className="vv-card p-6">
            <div className="w-11 h-11 rounded-full bg-amber-50 border border-amber-100 grid place-items-center text-[color:var(--vv-gold-deep)]"><s.icon size={20} /></div>
            <div className="text-xs uppercase tracking-widest text-stone-500 mt-4">Step {i + 1}</div>
            <div className="font-display text-xl mt-1">{s.title}</div>
            <div className="text-sm text-stone-600 mt-2 leading-relaxed">{s.desc}</div>
          </div>
        ))}
      </section>

      <section className="vv-container pb-14 grid md:grid-cols-2 gap-6">
        <div className="vv-card p-8">
          <h2 className="font-display text-2xl">What we treat by telehealth</h2>
          <ul className="mt-4 space-y-2 text-stone-700 text-sm">
            <li>• Medical weight management & metabolic support</li>
            <li>• Hormone optimization and andropause evaluations</li>
            <li>• Recovery, sleep, and longevity discussions</li>
            <li>• Sexual wellness consultations</li>
            <li>• Aesthetic & hair-restoration options</li>
          </ul>
        </div>
        <div className="vv-card p-8">
          <h2 className="font-display text-2xl">Good to know</h2>
          <ul className="mt-4 space-y-2 text-stone-700 text-sm">
            <li>• Self-pay practice — insurance not billed for these services.</li>
            <li>• HSA/FSA may cover eligible services; check your plan.</li>
            <li>• Recent labs may be reviewed, or the provider may order new testing.</li>
            <li>• Prescriptions (if approved) can be shipped discreetly where appropriate.</li>
            <li>• State licensure applies — the provider will confirm eligibility.</li>
          </ul>
        </div>
      </section>

      <section className="vv-container pb-16 grid md:grid-cols-2 gap-6">
        <CallCard location="telemedicine" title="Have questions before scheduling?" subtitle="Call a VidaVital care coordinator." />
        <Link to="/request-consultation" className="rounded-2xl bg-[color:var(--vv-onyx)] text-white p-6 md:p-7 flex flex-col justify-center hover:bg-[color:var(--vv-onyx-2)] transition-colors">
          <div className="text-xs uppercase tracking-widest text-amber-200">Ready when you are</div>
          <div className="font-display text-2xl md:text-3xl mt-2">Schedule a telemedicine visit</div>
        </Link>
      </section>
      <div className="vv-container pb-14"><Disclaimer /></div>
    </div>
  );
}
